import type { HelpGuide } from '../types';

export const cfforecastGuide: HelpGuide = {
  key: 'cfforecast',
  title: 'ประมาณการกระแสเงินสด (Cashflow Forecast)',
  subtitle: 'เมนู: ภาพรวม ▸ Cashflow Forecast',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/cfforecast/คู่มือการใช้งาน_ประมาณการกระแสเงินสด_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ประมาณการกระแสเงินสด (Cashflow Forecast)” ใช้คาดการณ์เงินสดรับ-จ่าย 13 สัปดาห์ข้างหน้า เพื่อให้เห็นล่วงหน้าว่าสัปดาห์ไหนเงินสดอาจตึงตัว จะได้วางแผนเร่งเก็บหนี้หรือเลื่อนการจ่ายได้ทัน ระบบดึงข้อมูลจริงจากใบแจ้งหนี้ลูกหนี้ที่ยังค้าง (AR) · ใบแจ้งหนี้เจ้าหนี้ที่ยังค้าง (AP) · ค่าเช่าตามสัญญาเช่า และสัญญาบริการ (OpContract) มาประมาณการอัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'หลักการ: ระบบประมาณการแบบทบยอดสัปดาห์ต่อสัปดาห์ — เงินสดปลายสัปดาห์ = เงินสดต้นสัปดาห์ + เงินสดรับ − เงินสดจ่าย แล้วยกยอดปลายเป็นต้นของสัปดาห์ถัดไปจนครบ 13 สัปดาห์ · หน้านี้เป็นรายงานคาดการณ์เพื่อวางแผน ไม่ได้ลงบัญชีหรือออกใบสำคัญใด ๆ',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Weekly Forecast (13 สัปดาห์) — ',
              text: 'กราฟเงินสดคงเหลือ + ตารางคาดการณ์รายสัปดาห์แยกตามหมวด (แท็บเริ่มต้น)',
            },
            {
              lead: 'แยกตามหมวด Cash In/Out — ',
              text: 'สรุปเงินสดรับตามแหล่งที่มา และเงินสดจ่ายตามวัตถุประสงค์ พร้อม %',
            },
            {
              lead: 'Scenario Analysis — ',
              text: 'วิเคราะห์สถานการณ์จำลอง Best / Base / Worst และปรับตัวคูณรายได้/ค่าใช้จ่ายเอง',
            },
            {
              lead: 'Forecast vs Actual — ',
              text: 'เปรียบเทียบยอดคาดการณ์กับยอดจริง 4 สัปดาห์ย้อนหลัง พร้อมความแม่นยำ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/cfforecast/01-overview.png',
          caption:
            'ภาพรวม — KPI เงินสดต้นงวด/รับ 13W/จ่าย 13W/ปลายงวด · คำเตือนเงินสดต่ำ และกราฟเงินสดคงเหลือ 13 สัปดาห์',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'เงินสดต้นงวด — ', text: 'เงินสดคงเหลือ ณ วันเริ่มต้นคาดการณ์ (ฐานการคำนวณ)' },
            {
              lead: 'เงินสดรับ 13W — ',
              text: 'เงินสดรับรวมที่คาดว่าจะได้ตลอด 13 สัปดาห์ พร้อมค่าเฉลี่ยต่อสัปดาห์',
            },
            {
              lead: 'เงินสดจ่าย 13W — ',
              text: 'เงินสดจ่ายรวมที่คาดว่าจะจ่ายตลอด 13 สัปดาห์ พร้อมค่าเฉลี่ยต่อสัปดาห์',
            },
            {
              lead: 'เงินสดปลายงวด — ',
              text: 'เงินสดคงเหลือคาดการณ์ ณ สิ้น 13 สัปดาห์ พร้อมยอดต่ำสุดและสัปดาห์ที่ต่ำสุด',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มและตัวเลือกมุมขวาบน: เลือกช่วงคาดการณ์ (13 สัปดาห์ / 6 เดือน / 12 เดือน) · Export (ส่งออก — อยู่ระหว่างพัฒนา ยังไม่เปิดใช้) · Refresh Forecast (ปุ่มหลัก — คำนวณประมาณการใหม่ 13 สัปดาห์)',
        },
        {
          kind: 'callout',
          text: 'แถบคำเตือนเงินสดต่ำ: เมื่อเงินสดคงเหลือคาดการณ์ในสัปดาห์ใดต่ำกว่าเกณฑ์ขั้นต่ำ (200,000 บาท) ระบบจะขึ้นแถบเตือนสีส้มระบุสัปดาห์ที่จะต่ำ พร้อมข้อเสนอแนะให้เร่งเก็บหนี้หรือเลื่อนการจ่าย',
        },
      ],
    },
    {
      id: 'weekly',
      heading: '3. แท็บ Weekly Forecast — ตารางรายสัปดาห์',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Weekly Forecast (13 สัปดาห์) แสดงการ์ดบน “กราฟเงินสดคงเหลือ 13 สัปดาห์” (แท่ง W1–W13 แท่งสีแดงเมื่อต่ำกว่าเกณฑ์ ส้มเมื่อใกล้เกณฑ์) และการ์ดล่างเป็นตารางคาดการณ์รายสัปดาห์ละเอียด',
        },
        {
          kind: 'para',
          text: 'ตารางมีคอลัมน์ รายการ (ตรึงซ้าย) · W1–W13 (สัปดาห์ละคอลัมน์ พร้อมวันเริ่มสัปดาห์) · รวม (ยอดรวม 13 สัปดาห์ของแต่ละหมวด) แบ่งเป็นกลุ่ม เงินสดรับ (รับชำระจากลูกหนี้ · ขายเงินสด · รายได้อื่น) → รวมเงินสดรับ และกลุ่ม เงินสดจ่าย (จ่ายเจ้าหนี้ · เงินเดือนพนักงาน · ค่าสาธารณูปโภค · ภาษีและเงินสมทบ · ลงทุนสินทรัพย์ · ชำระเงินกู้ + ดอกเบี้ย) → รวมเงินสดจ่าย ปิดท้ายด้วยแถว Net Cashflow และ เงินสดคงเหลือปลายสัปดาห์',
        },
        {
          kind: 'image',
          src: '/help/cfforecast/02-weekly-matrix.png',
          caption:
            'ตารางรายสัปดาห์ครบ 13 สัปดาห์ — แยกเงินสดรับ-จ่ายตามหมวด พร้อมยอดรวม Net Cashflow และเงินสดคงเหลือปลายสัปดาห์',
        },
      ],
    },
    {
      id: 'category',
      heading: '4. แท็บ แยกตามหมวด Cash In/Out',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ แยกตามหมวด Cash In/Out สรุปภาพรวมว่าเงินสดรับมาจากแหล่งใดบ้าง และเงินสดจ่ายไปกับอะไรบ้าง แบ่งเป็น 2 การ์ดคู่กัน',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เงินสดรับตามแหล่งที่มา — ',
              text: 'แต่ละหมวดรับ พร้อม 13 สัปดาห์รวม · เฉลี่ย/สัปดาห์ · % ของยอดรับทั้งหมด',
            },
            {
              lead: 'เงินสดจ่ายตามวัตถุประสงค์ — ',
              text: 'แต่ละหมวดจ่าย พร้อม 13 สัปดาห์รวม · เฉลี่ย/สัปดาห์ · % ของยอดจ่ายทั้งหมด',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/cfforecast/03-category.png',
          caption:
            'สรุปแยกตามหมวด — เงินสดรับตามแหล่งที่มา (ซ้าย) และเงินสดจ่ายตามวัตถุประสงค์ (ขวา) พร้อมยอดรวม เฉลี่ย/สัปดาห์ และ %',
        },
      ],
    },
    {
      id: 'scenario',
      heading: '5. แท็บ Scenario Analysis — สถานการณ์จำลอง',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Scenario Analysis เปรียบเทียบ 3 สถานการณ์เพื่อทดสอบว่าเงินสดปลายงวดจะเป็นอย่างไรในกรณีต่าง ๆ แต่ละแถวแสดง สมมติฐาน · รับ 13W · จ่าย 13W · เงินสดปลายงวด · ระดับความเสี่ยง (ต่ำ / ปานกลาง / สูง · ต้องกู้)',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Best Case — ',
              text: 'เก็บหนี้เร็ว · ขายเงินสดเพิ่ม 20% · ค่าใช้จ่ายลด 5% (ความเสี่ยงต่ำ)',
            },
            { lead: 'Base Case — ', text: 'ตามแผนปัจจุบัน (ความเสี่ยงปานกลาง — แถวที่ไฮไลต์)' },
            {
              lead: 'Worst Case — ',
              text: 'เก็บหนี้ช้า · ขายลดลง 20% · ค่าใช้จ่ายเพิ่ม 5% (ความเสี่ยงสูง · ต้องกู้)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'แต่ละแถวปรับตัวคูณ rev (รายได้) / exp (ค่าใช้จ่าย) เองได้ผ่านช่องตัวเลข แล้วระบบจะคำนวณเงินสดปลายงวดใหม่ทันที หมายเหตุ: รายการ MANUAL (เพิ่มเอง) และรายได้อื่นจะไม่ถูกสเกล เพราะถือเป็นกระแสเงินสดที่ทราบแน่นอนแล้ว',
        },
        {
          kind: 'image',
          src: '/help/cfforecast/04-scenario.png',
          caption:
            'วิเคราะห์สถานการณ์จำลอง Best / Base / Worst — ปรับตัวคูณ rev/exp และดูเงินสดปลายงวดกับระดับความเสี่ยง',
        },
      ],
    },
    {
      id: 'variance',
      heading: '6. แท็บ Forecast vs Actual — เทียบคาดการณ์กับจริง',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Forecast vs Actual ย้อนดู 4 สัปดาห์ที่ผ่านมาว่ายอดที่เคยคาดการณ์ไว้ตรงกับยอดจริงที่ลงบัญชีแล้วแค่ไหน เพื่อประเมินความแม่นยำของการคาดการณ์ แต่ละแถวแสดง สัปดาห์ · Forecast In / Actual In / Variance In · Forecast Out / Actual Out / Variance Out · Accuracy (ความแม่นยำ %)',
        },
        {
          kind: 'para',
          text: 'หัวการ์ดแสดงความแม่นยำเฉลี่ยทั้งช่วง และด้านล่างสรุปสัปดาห์ที่แม่นที่สุดและคลาดเคลื่อนที่สุด ใช้เป็นข้อมูลปรับปรุงสมมติฐานการคาดการณ์ในรอบถัดไป',
        },
        {
          kind: 'image',
          src: '/help/cfforecast/05-variance.png',
          caption:
            'เปรียบเทียบยอดคาดการณ์ vs จริง 4 สัปดาห์ย้อนหลัง — Variance เงินสดรับ/จ่าย และความแม่นยำรายสัปดาห์',
        },
      ],
    },
    {
      id: 'refresh',
      heading: '7. การคำนวณใหม่ และการเพิ่มรายการเอง',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม Refresh Forecast เพื่อให้ระบบคำนวณประมาณการ 13 สัปดาห์ใหม่จากข้อมูลล่าสุด (ใบแจ้งหนี้ลูกหนี้/เจ้าหนี้ที่ยังค้าง · ค่าเช่า · สัญญาบริการ) การคำนวณใหม่จะแทนที่รายการคาดการณ์อัตโนมัติเดิม แต่จะคงรายการที่ผู้ใช้เพิ่มเองไว้',
        },
        {
          kind: 'para',
          text: 'หากยังไม่เคยกด ระบบจะขึ้นข้อความ “ยังไม่มี Cashflow Forecast snapshot — กดปุ่ม Refresh Forecast เพื่อสร้างรายการแรก” ให้กดปุ่มเพื่อสร้างประมาณการรอบแรก',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'กด Add manual entry (ที่แท็บ Weekly)',
              text: 'ใช้เพิ่มกระแสเงินสดที่ทราบล่วงหน้าแต่ระบบยังไม่เห็น เช่น เงินเข้า/ออกพิเศษ',
            },
            {
              title: 'เลือกหมวด',
              text: 'AR_COL / CASH_SALE / OTHER_IN / AP_PAY / PAYROLL / UTILITY / TAX / CAPEX / LOAN',
            },
            { title: 'ระบุสัปดาห์ (1–13)', text: 'สัปดาห์ที่ต้องการลงรายการ' },
            {
              title: 'ระบุจำนวนเงิน (มีเครื่องหมาย)',
              text: 'เงินรับใส่ค่าบวก · เงินจ่ายใส่ค่าลบ',
            },
            { title: 'ระบุคำอธิบาย', text: 'คำอธิบายรายการ (ค่าตั้งต้น “Manual adjustment”)' },
          ],
        },
        {
          kind: 'para',
          text: 'รายการที่เพิ่มเอง (MANUAL) จะถูกคงไว้ทุกครั้งที่กด Refresh Forecast และหากต้องการลบ ลบได้เฉพาะรายการที่เพิ่มเองเท่านั้น (รายการที่ระบบดึงมาให้ต้องแก้โดยการคำนวณใหม่)',
        },
        {
          kind: 'callout',
          text: 'หน้านี้เปิดดูได้ทุกผู้ใช้ แต่การกด Refresh Forecast และการเพิ่ม/ลบรายการเองต้องมีสิทธิ์ cashflow.manage',
          variant: 'info',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '8. ความหมายของป้ายความแม่นยำและความเสี่ยง',
      blocks: [
        {
          kind: 'table',
          head: ['ป้าย', 'เกณฑ์', 'ความหมาย'],
          rows: [
            ['ความแม่นยำ (เขียว)', '≥ 95%', 'คาดการณ์แม่นยำสูง ใกล้เคียงยอดจริงมาก'],
            ['ความแม่นยำ (ส้ม)', '90% – 94.9%', 'คาดการณ์พอใช้ ควรทบทวนสมมติฐาน'],
            ['ความแม่นยำ (แดง)', '< 90%', 'คาดการณ์คลาดเคลื่อนมาก ต้องปรับสมมติฐาน'],
            ['ความเสี่ยง ต่ำ', 'Best Case', 'เงินสดเพียงพอ ไม่ต้องกังวล'],
            ['ความเสี่ยง ปานกลาง', 'Base Case', 'ตามแผนปัจจุบัน ควรเฝ้าระวัง'],
            ['ความเสี่ยง สูง · ต้องกู้', 'Worst Case', 'เงินสดอาจไม่พอ อาจต้องจัดหาเงินกู้'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '9. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'เปิดหน้าแล้วไม่มีข้อมูลเลย ขึ้นว่า “ยังไม่มี snapshot”?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กดปุ่ม Refresh Forecast เพื่อสร้างประมาณการ 13 สัปดาห์รอบแรก ระบบยังไม่คำนวณให้อัตโนมัติจนกว่าจะกด',
            },
          ],
        },
        { kind: 'para', text: 'กด Refresh แล้วรายการที่เพิ่มเองจะหายไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่หาย — การคำนวณใหม่แทนที่เฉพาะรายการอัตโนมัติ (AR/AP/ค่าเช่า/สัญญา) ส่วนรายการ MANUAL จะถูกคงไว้เสมอ',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปรับ rev/exp ในแท็บ Scenario แล้ว ตัวเลขในแท็บอื่นเปลี่ยนด้วยหรือไม่?',
        },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่เปลี่ยน — Scenario เป็นการจำลองเฉพาะในแท็บนั้น ไม่กระทบประมาณการหลักหรือยอดในแท็บ Weekly',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม Export กดแล้วไม่ทำงาน?' },
        {
          kind: 'bullets',
          items: [{ text: 'ฟังก์ชันส่งออกยังอยู่ระหว่างพัฒนา ยังไม่เปิดใช้ในรอบนี้' }],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '10. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Cashflow Forecast — ',
              text: 'การคาดการณ์เงินสดรับ-จ่ายล่วงหน้า 13 สัปดาห์เพื่อวางแผนสภาพคล่อง',
            },
            {
              lead: 'เงินสดต้นงวด / ปลายงวด — ',
              text: 'เงินสดคงเหลือ ณ วันเริ่ม และ ณ สิ้นช่วงคาดการณ์',
            },
            {
              lead: 'Net Cashflow — ',
              text: 'เงินสดรับสุทธิของสัปดาห์ (เงินสดรับ − เงินสดจ่าย)',
            },
            {
              lead: 'Scenario (Best/Base/Worst) — ',
              text: 'สถานการณ์จำลองที่ปรับตัวคูณรายได้/ค่าใช้จ่ายเพื่อทดสอบสมมติฐาน',
            },
            {
              lead: 'rev / exp multiplier — ',
              text: 'ตัวคูณรายได้/ค่าใช้จ่ายในสถานการณ์จำลอง (เช่น 1.2 = +20%, 0.95 = −5%)',
            },
            {
              lead: 'Variance — ',
              text: 'ผลต่างระหว่างยอดคาดการณ์กับยอดจริง',
            },
            {
              lead: 'Accuracy (ความแม่นยำ) — ',
              text: 'ระดับที่ยอดคาดการณ์ตรงกับยอดจริง คิดเป็น %',
            },
            {
              lead: 'MANUAL entry — ',
              text: 'รายการกระแสเงินสดที่ผู้ใช้เพิ่มเอง คงอยู่ทุกครั้งที่คำนวณใหม่',
            },
          ],
        },
      ],
    },
  ],
};
